import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { createSelectOptionsFromEnum } from 'src/app/common/selectOptionsUtils';
import {
  BusinessCriticality,
  LabelModel,
  Platform,
  ResourceType,
  SecurityRisk,
  ResourceTypesPerPlatform,
} from 'src/app/models/api/common';
import { PolicyModel } from 'src/app/models/api/governance/policy.model';
import { BaseViolationRule, RuleTag, Severity } from 'src/app/models/api/governance/violationRule.model';
import { ResourceStatus } from 'src/app/models/api/inventoryResource.model';
import { NewViolationFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newViolationTriggerModel';
import { PlaybooksTriggerConfigInput } from 'src/app/models/componentInputs/playbooksTriggerInput.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { configurationFieldState } from 'src/app/components/playbooks/playbooks-trigger/triggers/common/enums/configuration-field-state';
import { newViolationConfigurationField } from 'src/app/components/playbooks/playbooks-trigger/triggers/new-violation-trigger/enums/new-violation-configuration-field';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { OwaspSelectOptions } from 'src/app/models/selectOptions/owaspSelectOptions.model';
import _ from 'lodash';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { AppValidators } from 'src/app/common/appValidators';
import { GenerativeAiOptionsSelectOptions } from 'src/app/models/selectOptions/generativeAiOptionsSelectOptions.model';
import { RuleTagDisplayNamePipe } from 'src/app/pipes/rule-tag-display-name/rule-tag-display-name.pipe';
import { BuiltinLabelIds } from 'src/app/models/constants/builtinLabelIds.constants';
import { LabelInclusionStatus } from 'src/app/models/api/playbooks/triggers/labelInclusionStatus.enum';
import { PlaybookLabelFilterModel } from 'src/app/models/api/playbooks/triggers/playbookLabelFilterModel';
import { Label } from '../../../../../labels/englishLabels';
import {
  unsupportedResourceTypesForViolationByPlatform,
  unsupportedAllResourceTypesForViolation,
} from '../common/consts';
import { ResourceTypeDisplayNamePipe } from '../../../../../pipes/resource-type-display-name-pipe/resource-type-display-name-pipe';
import { createSelectOptionsFromStringArray } from 'src/app/common/selectOptionsUtils';

@Component({
  selector: 'app-new-violation-trigger',
  templateUrl: './new-violation-trigger.component.html',
  styleUrls: ['./new-violation-trigger.component.scss'],
})
export class NewViolationTriggerComponent implements OnInit, OnDestroy {
  @Input() public fg: UntypedFormGroup;
  @Input() public playbooksTriggerInput: PlaybooksTriggerConfigInput;
  @Output() public validConfiguration = new EventEmitter<boolean>();
  public violationRuleUpdatesEnabled$ = new BehaviorSubject<boolean>(false);
  public violationRuleUpdatesTooltipText = Label.get('playbooks.triggersConfigurationLabels.ruleUpdates.tooltip');

  public violationRulesOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public categorizedViolationRulesOptions$: {
    [category: string]: BehaviorSubject<ISelectOption>[];
  } = {};
  public categorizedOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public labelsOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public severityOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>(
    createSelectOptionsFromEnum(Severity, this._beautifyPipe)
  );
  public ruleTagOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>(
    this._createRuleTagsSelectOptions()
  );
  public resourceStatusOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>(
    createSelectOptionsFromEnum(ResourceStatus, this._beautifyPipe)
  );
  public resourceSecurityRiskOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>(
    createSelectOptionsFromEnum(SecurityRisk, this._beautifyPipe)
  );
  public resourceBusinessCriticalityOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>(
    createSelectOptionsFromEnum(BusinessCriticality, this._beautifyPipe)
  );
  public resourceGenerativeAiOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>(
    this._createGenerativeAiOptionsSelectOptions()
  );
  public connectorsOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public configurationFieldsOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public resourceTypeOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  public multipleResourceTypesWarningVisible = false;
  public multipleResourceTypesFromRulesWarningVisible = false;
  public createdSinceDatesBehavioralSubject$: BehaviorSubject<Date | undefined>;
  public createdUntilDatesBehavioralSubject$: BehaviorSubject<Date | undefined>;
  public firstSeenSinceDatesBehavioralSubject$: BehaviorSubject<Date | undefined>;
  public firstSeenUntilDatesBehavioralSubject$: BehaviorSubject<Date | undefined>;

  public allConfigurationFieldsState: {
    [id: string]: configurationFieldState;
  };
  public selectedFieldToAdd?: string;
  public selectedConfiguration: string[] = [];
  public labelStatusChecked = false;

  public newViolationConfigurationFieldEnum = newViolationConfigurationField;
  public configurationFieldStateEnum = configurationFieldState;
  public selectedLabelsInclusionStatus: LabelInclusionStatus;
  public LabelInclusionStatusEnum = LabelInclusionStatus;
  public containsDeletedLabels = false;

  private _newViolationTriggerModel: NewViolationFoundTriggerConfigModel = { ruleUpdatesEnabled: false };
  private _baseViolationRulesSubscription$: Subscription;
  private _labelsSubscription$: Subscription;
  private _baseViolationRules: BaseViolationRule[] = [];
  private _alreadyInitialized = false;

  private _isNewTrigger = false;
  private readonly _defaultLabelsFilter = this._getLabelFilterModel(
    [BuiltinLabelIds.FALSE_POSITIVE],
    LabelInclusionStatus.exclude
  );

  constructor(
    private readonly _store: StoreService,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _beautifyPipe: BeautifyDisplayStringPipe,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _resourceTypeDisplayNamePipe: ResourceTypeDisplayNamePipe,
    private readonly _ruleTagDisplayName: RuleTagDisplayNamePipe
  ) {}

  ngOnInit(): void {
    const allowedResourceTypes = this._filterResourceTypesByPlatform();
    const resourceTypeOptions: ISelectOption[] = createSelectOptionsFromStringArray(
      allowedResourceTypes,
      this._resourceTypeDisplayNamePipe
    );
    this.resourceTypeOptions$ = new BehaviorSubject<ISelectOption[]>(resourceTypeOptions);

    this._getBaseCategorizedViolationRulesOptions();
    this._createLabelsSelectOptions();
    this._geConnectorsOptions();

    this._newViolationTriggerModel = { ruleUpdatesEnabled: false };
    let filteredPlaybookLabels = this._newViolationTriggerModel?.labels;

    if (!this.playbooksTriggerInput.triggerConfig) {
      this._isNewTrigger = true;
    } else {
      this._newViolationTriggerModel = {
        ...this.playbooksTriggerInput.triggerConfig,
      } as NewViolationFoundTriggerConfigModel;
      filteredPlaybookLabels = this._newViolationTriggerModel.labels;

      // filter out labels that were deleted
      if (this._newViolationTriggerModel.labels) {
        const playbookLabelsIds = this._newViolationTriggerModel.labels.labelsIds;
        const filteredPlaybookLabelsIds = playbookLabelsIds.filter(
          (labelId) => this.labelsOptions$.value.map((option) => option.id).indexOf(labelId) > -1
        );
        if (filteredPlaybookLabelsIds.length !== playbookLabelsIds.length) {
          this.containsDeletedLabels = true;
          filteredPlaybookLabels = this._getLabelFilterModel(
            filteredPlaybookLabelsIds,
            this._newViolationTriggerModel.labels.inclusionStatus
          );
        }
      }

      this.violationRuleUpdatesEnabled$.next(this._newViolationTriggerModel.ruleUpdatesEnabled);
    }

    this.fg.addControl('ruleUpdatesEnabled', new UntypedFormControl(this._newViolationTriggerModel.ruleUpdatesEnabled));
    this.fg.addControl('ruleIds', new UntypedFormControl(this._newViolationTriggerModel?.ruleIds || []));
    this.fg.addControl('ruleTags', new UntypedFormControl(this._newViolationTriggerModel?.ruleTags || []));
    this.fg.addControl('severity', new UntypedFormControl(this._newViolationTriggerModel?.severity || []));
    this.fg.addControl('connectors', new UntypedFormControl(this._newViolationTriggerModel?.connectors || []));
    this.fg.addControl('resourceStatus', new UntypedFormControl(this._newViolationTriggerModel?.resourceStatus || []));
    this.fg.addControl(
      'resourceCreatedBy',
      new UntypedFormControl(this._newViolationTriggerModel?.resourceCreatedBy || '', [AppValidators.email])
    );
    this.fg.addControl(
      'resourceLastModifiedBy',
      new UntypedFormControl(this._newViolationTriggerModel?.resourceLastModifiedBy || '')
    );
    this.fg.addControl(
      'resourceCreatedSince',
      new UntypedFormControl(this._newViolationTriggerModel?.resourceCreatedSince)
    );
    this.fg.addControl(
      'resourceCreatedUntil',
      new UntypedFormControl(this._newViolationTriggerModel?.resourceCreatedUntil)
    );
    this.fg.addControl(
      'resourceFirstSeenSince',
      new UntypedFormControl(this._newViolationTriggerModel?.resourceFirstSeenSince)
    );
    this.fg.addControl(
      'resourceFirstSeenUntil',
      new UntypedFormControl(this._newViolationTriggerModel?.resourceFirstSeenUntil)
    );
    this.fg.addControl(
      'resourceSecurityRisk',
      new UntypedFormControl(this._newViolationTriggerModel?.resourceSecurityRisk || [])
    );
    this.fg.addControl(
      'resourceBusinessCriticality',
      new UntypedFormControl(this._newViolationTriggerModel?.resourceBusinessCriticality || [])
    );
    this.fg.addControl(
      'resourceGenerativeAiOptions',
      new UntypedFormControl(this._newViolationTriggerModel?.resourceGenerativeAiOptions || [])
    );
    this.fg.addControl('resourceTypes', new UntypedFormControl(this._newViolationTriggerModel?.resourceTypes || []));
    this.fg.addControl('labels', new UntypedFormControl(filteredPlaybookLabels));
    this.fg.addControl(
      'labelInclusionStatusControl',
      new UntypedFormControl(this._newViolationTriggerModel?.labels?.inclusionStatus)
    );

    this.fg.get('labelInclusionStatusControl')?.valueChanges.subscribe((value) => {
      this.fg.controls['labels'].setValue(
        this._getLabelFilterModel(this.fg.controls['labels'].value?.labelsIds || [], value)
      );
    });
    if (this._newViolationTriggerModel?.labels?.inclusionStatus) {
      this.selectedLabelsInclusionStatus = this._newViolationTriggerModel.labels.inclusionStatus;
    }

    this.createdSinceDatesBehavioralSubject$ = new BehaviorSubject<Date | undefined>(
      this.fg.controls['resourceCreatedSince'].value
    );
    this.createdUntilDatesBehavioralSubject$ = new BehaviorSubject<Date | undefined>(
      this.fg.controls['resourceCreatedUntil'].value
    );
    this.firstSeenSinceDatesBehavioralSubject$ = new BehaviorSubject<Date | undefined>(
      this.fg.controls['resourceFirstSeenSince'].value
    );
    this.firstSeenUntilDatesBehavioralSubject$ = new BehaviorSubject<Date | undefined>(
      this.fg.controls['resourceFirstSeenUntil'].value
    );

    this._resetConfigurationFieldsState();
    const selectedResourceTypes: Set<ResourceType> = new Set(
      this._baseViolationRules
        .filter((rule) => this.fg.controls['ruleIds'].value.includes(rule.id))
        .map((rule) => rule.resourceType)
    );
    this._setIrrelevantConfigurationFields(selectedResourceTypes);
    this._initSelectedConfigurationFields(this._newViolationTriggerModel);
    this._setConfigurationFieldsOptions();

    this.fg.controls['resourceCreatedBy'].valueChanges.subscribe(() =>
      this.validConfiguration.emit(this._checkFormIsValid())
    );
    this.fg.controls['resourceLastModifiedBy'].valueChanges.subscribe(() =>
      this.validConfiguration.emit(this._checkFormIsValid())
    );

    if (this._isNewTrigger) {
      this.fg.controls['ruleTags'].setValue([RuleTag.generalAvailability]);
      this.fg.controls['labels'].setValue(this._defaultLabelsFilter);
      this.fg.controls['labelInclusionStatusControl'].setValue(LabelInclusionStatus.exclude);
      this.selectedLabelsInclusionStatus = LabelInclusionStatus.exclude;
    }
  }

  ngOnDestroy(): void {
    this._baseViolationRulesSubscription$?.unsubscribe();
    this._labelsSubscription$?.unsubscribe();
  }

  public dateSelected(controlName: string, value?: Date): void {
    this.fg.controls[controlName].setValue(value);
    this.validConfiguration.emit(this._checkFormIsValid());
  }

  public updateSelectControl(controlName: string, option?: ISelectOption) {
    if (option) {
      this.fg.controls[controlName].setValue(option.id);
    } else {
      this.fg.controls[controlName].setValue(undefined);
    }
    this.validConfiguration.emit(this._checkFormIsValid());
  }

  public updateLabelsControl(options: ISelectOption[]) {
    this.fg.controls['labels'].setValue(
      this._getLabelFilterModel(
        options.map((option) => option.id),
        this.fg.controls['labels'].value.inclusionStatus
      )
    );
    this.validConfiguration.emit(this._checkFormIsValid());
  }

  public updateMultiSelectControl(controlName: string, options: ISelectOption[]) {
    // Filter out undefined or null values
    const validOptions = options.filter((option) => option?.id !== undefined && option.id !== null);
    if (controlName === 'resourceTypes') {
      this._handleResourceTypesControlUpdate(validOptions);
    } else if (controlName == 'ruleIds') {
      this._handleRuleIdsControlUpdate(validOptions);
    } else {
      this.fg.controls[controlName].setValue(validOptions.map((option) => option.id));
    }

    this.validConfiguration.emit(this._checkFormIsValid());
  }

  private _handleResourceTypesControlUpdate(validOptions: ISelectOption[]): void {
    const allowedResourceTypes = this._filterResourceTypesByPlatform();
    const filteredOptions = validOptions.filter((option) => allowedResourceTypes.includes(option.id as ResourceType));
    this.fg.controls['resourceTypes'].setValue(filteredOptions.map((option) => option.id));

    const selectedResourceTypes: Set<ResourceType> = new Set(this.fg.controls['resourceTypes'].value);
    this.multipleResourceTypesWarningVisible = selectedResourceTypes.size > 1;
    this._setIrrelevantConfigurationFields(selectedResourceTypes);
    this._setConfigurationFieldsOptions();
  }

  private _filterResourceTypesByPlatform(): ResourceType[] {
    const integrationPlatform = this.playbooksTriggerInput.integration.platform;
    const unsupportedResourceTypes: ResourceType[] =
      unsupportedResourceTypesForViolationByPlatform[integrationPlatform] ?? unsupportedAllResourceTypesForViolation;
    const platformResourceTypes = ResourceTypesPerPlatform[integrationPlatform] ?? [];
    const allowedResourceTypes = Object.values(ResourceType)
      .filter(
        (value) =>
          !unsupportedResourceTypes.includes(value as ResourceType) &&
          platformResourceTypes.includes(value as ResourceType)
      )
      .map((value) => value as ResourceType);

    return allowedResourceTypes;
  }

  private _handleRuleIdsControlUpdate(validOptions: ISelectOption[]): void {
    this.fg.controls['ruleIds'].setValue(validOptions.map((option) => option.id));
    const selectedResourceTypes: Set<ResourceType> = new Set(
      this._baseViolationRules
        .filter((rule) => this.fg.controls['ruleIds'].value.includes(rule.id))
        .map((rule) => rule.resourceType)
    );

    this.multipleResourceTypesFromRulesWarningVisible = selectedResourceTypes.size > 1;
    this._setIrrelevantConfigurationFields(selectedResourceTypes);
    this._setConfigurationFieldsOptions();
  }

  public addSetting() {
    if (this.selectedFieldToAdd) {
      this.allConfigurationFieldsState[this.selectedFieldToAdd] = configurationFieldState.selected;
      this.selectedConfiguration = [this.selectedFieldToAdd].concat(this.selectedConfiguration);
      this._setConfigurationFieldsOptions();
      this.validConfiguration.emit(false);
    }
  }

  public removeSetting(fieldToRemove: string) {
    this._resetSettingState(fieldToRemove, configurationFieldState.unselected);
    this._setConfigurationFieldsOptions();
    this.validConfiguration.emit(this._checkFormIsValid());
  }

  public onConfigurationFieldSelection(selectedField?: ISelectOption) {
    this.selectedFieldToAdd = selectedField?.value;
    this.addSetting();
  }

  public onRuleUpdatesToggled(value: boolean): void {
    this.violationRuleUpdatesEnabled$.next(value);
    this.fg.controls['ruleUpdatesEnabled'].setValue(value);
  }

  private _setConfigurationFieldsOptions() {
    this.selectedFieldToAdd = undefined;
    const unselectedFields = [];

    for (const field in this.allConfigurationFieldsState) {
      if (this.allConfigurationFieldsState[field] == configurationFieldState.unselected) {
        unselectedFields.push({
          id: field,
          value: field,
          displayName: this._labelsProviderPipe.transform(`playbooks.triggersConfigurationLabels.${field}`),
        } as ISelectOption);
      }
    }

    this.configurationFieldsOptions$.next(unselectedFields);
  }

  private _getBaseCategorizedViolationRulesOptions() {
    this._baseViolationRulesSubscription$ = this._store.baseViolationRule$.subscribe((rules) => {
      this._baseViolationRules = rules;
      const rulesToDisplay = rules.map((rule) => ({
        ...rule,
        owasp: rule.owasp[0],
      }));
      this.categorizedViolationRulesOptions$ = _.mapValues(_.groupBy(rulesToDisplay, 'owasp'), (rulesToDisplay) =>
        rulesToDisplay
          .filter((rulesToDisplay) => rulesToDisplay.platform == this.playbooksTriggerInput.integration.platform)
          .map((rulesToDisplay) => {
            const viewModel: ISelectOption = {
              id: rulesToDisplay.id,
              value: rulesToDisplay.id,
              displayName: `${rulesToDisplay.id} - ${rulesToDisplay.displayName}`,
            } as ISelectOption;
            return new BehaviorSubject(viewModel);
          })
      );
    });
    this._transformCategorizedOptions();
  }

  private _createLabelsSelectOptions() {
    const labels_options: ISelectOption[] = [];
    this._labelsSubscription$ = this._store.labels$.subscribe((labels: LabelModel[]) => {
      labels.forEach((label: LabelModel) => {
        labels_options.push({
          id: label.id,
          value: label.id,
          displayName: label.displayName,
        } as ISelectOption);
      });
      this.labelsOptions$.next(labels_options);
    });
  }

  private _transformCategorizedOptions() {
    const owaspOptions = _.cloneDeep(OwaspSelectOptions).filter(
      (option) => Object.keys(this.categorizedViolationRulesOptions$).indexOf(option.value) > -1
    );
    const optionsWithCategories: ISelectOption[] = [];
    owaspOptions.forEach((category) => {
      if (category.displayName) {
        this.categorizedViolationRulesOptions$[category.value].forEach((option) => {
          optionsWithCategories.push({
            id: option.value.id,
            value: option.value.id,
            displayName: option.value.displayName,
            categoryName: category.displayName,
          } as ISelectOption);
        });
      }
    });
    this.categorizedOptions$.next(optionsWithCategories);
  }

  private _geConnectorsOptions() {
    this._policiesHttpService.getIntegrationPolicies(this.playbooksTriggerInput.integration.id).subscribe(
      (response: PolicyModel[]) => {
        const connectors = response[0].generalConnectorList;
        const connectorsOptions = connectors.map(
          (connector) =>
            ({
              id: connector.id,
              value: connector.id,
              displayName: connector.displayName,
            }) as ISelectOption
        );

        this.connectorsOptions$.next(connectorsOptions);
      },
      (error) => this._httpErrorHandlerService.handle(error)
    );
  }

  private _resetConfigurationFieldsState(): void {
    this.allConfigurationFieldsState = {};

    for (const field in newViolationConfigurationField) {
      this.allConfigurationFieldsState[field] = configurationFieldState.unselected;
    }

    // if this is not the first time the page loads
    // need to reset the control values when visibility changes
    // otherwise controls will have wrong values
    if (this._alreadyInitialized) {
      this.fg.controls['ruleIds'].setValue([]);
      this.fg.controls['ruleTags'].setValue([]);
      this.fg.controls['resourceCreatedBy'].setValue('');
      this.fg.controls['resourceCreatedSince'].setValue(null);
      this.fg.controls['resourceCreatedUntil'].setValue(null);
      this.fg.controls['resourceStatus'].setValue(null);
      this.fg.controls['resourceSecurityRisk'].setValue([]);
      this.fg.controls['resourceBusinessCriticality'].setValue([]);
      this.fg.controls['resourceGenerativeAiOptions'].setValue([]);
      this.fg.controls['resourceLastModifiedBy'].setValue('');
      this.fg.controls['resourceFirstSeenSince'].setValue(null);
      this.fg.controls['resourceFirstSeenUntil'].setValue(null);
      this.updateMultiSelectControl('connectors', []);
      this.fg.controls['labels'].setValue(undefined);

      // remove date string in ui (date-picker)
      this.createdSinceDatesBehavioralSubject$.next(undefined);
      this.createdUntilDatesBehavioralSubject$.next(undefined);
      this.firstSeenSinceDatesBehavioralSubject$.next(undefined);
      this.firstSeenUntilDatesBehavioralSubject$.next(undefined);
    } else {
      this._alreadyInitialized = true;
    }
  }

  private _resetConfigurationFieldsRelevance(): void {
    if (this.allConfigurationFieldsState) {
      for (const field in newViolationConfigurationField) {
        if (this.allConfigurationFieldsState[field] == configurationFieldState.irrelevant) {
          this.allConfigurationFieldsState[field] = configurationFieldState.unselected;
        }
      }
    }
  }

  private _setIrrelevantConfigurationFields(selectedResourceTypes: Set<ResourceType>) {
    this._resetConfigurationFieldsRelevance();

    const selectedResourceTypesArray = [...selectedResourceTypes]; // needed to be able to use 'every' function

    const platform = this.playbooksTriggerInput.integration.platform;
    const ppResourceTypesSupportingConnectorsFiler = new Set<ResourceType>([
      ResourceType.connection,
      ResourceType.flow,
    ]);
    const ppResourceTypesSupportingGenAiFiler = new Set<ResourceType>([ResourceType.canvas_app, ResourceType.flow]);
    const workatoResourceTypesSupportingConnectorsFiler = new Set<ResourceType>([
      ResourceType.connection,
      ResourceType.recipe,
    ]);

    switch (platform) {
      case Platform.salesforce:
        this._resetSettingsState(
          [newViolationConfigurationField.connectors, newViolationConfigurationField.resourceGenerativeAiOptions],
          configurationFieldState.irrelevant
        );

        if (selectedResourceTypes.has(ResourceType.user)) {
          this._resetSettingsState(
            [newViolationConfigurationField.resourceBusinessCriticality],
            configurationFieldState.irrelevant
          );
        }
        break;

      case Platform.chatGptEnterprise:
        this._resetSettingsState(
          [newViolationConfigurationField.connectors, newViolationConfigurationField.resourceGenerativeAiOptions],
          configurationFieldState.irrelevant
        );
        break;

      case Platform.amazonBedrock:
        this._resetSettingsState(
          [newViolationConfigurationField.connectors, newViolationConfigurationField.resourceGenerativeAiOptions],
          configurationFieldState.irrelevant
        );
        break;

      case Platform.budibase:
        this._resetSettingsState(
          [newViolationConfigurationField.connectors, newViolationConfigurationField.resourceGenerativeAiOptions],
          configurationFieldState.irrelevant
        );
        break;

      case Platform.airtable:
        this._resetSettingsState(
          [newViolationConfigurationField.connectors, newViolationConfigurationField.resourceGenerativeAiOptions],
          configurationFieldState.irrelevant
        );
        break;

      case Platform.powerplatform:
        if (
          !(
            (selectedResourceTypes.size == 1 || selectedResourceTypes.size == 2) &&
            selectedResourceTypesArray.every((value) => ppResourceTypesSupportingConnectorsFiler.has(value))
          )
        ) {
          this._resetSettingsState([newViolationConfigurationField.connectors], configurationFieldState.irrelevant);
        }
        if (
          selectedResourceTypes.has(ResourceType.dlp_policy) ||
          selectedResourceTypes.has(ResourceType.dataverse_team) ||
          selectedResourceTypes.has(ResourceType.portal_page) ||
          selectedResourceTypes.has(ResourceType.connector) ||
          selectedResourceTypes.has(ResourceType.custom_connector)
        ) {
          this._resetSettingsState(
            [newViolationConfigurationField.resourceBusinessCriticality],
            configurationFieldState.irrelevant
          );
        }
        if (
          selectedResourceTypes.has(ResourceType.azure_active_directory_group) ||
          selectedResourceTypes.has(ResourceType.user) ||
          selectedResourceTypes.has(ResourceType.environment)
        ) {
          this._resetSettingsState(
            [
              newViolationConfigurationField.resourceBusinessCriticality,
              newViolationConfigurationField.resourceLastModifiedBy,
            ],
            configurationFieldState.irrelevant
          );
        }
        if (
          selectedResourceTypes.has(ResourceType.gateway_cluster) ||
          selectedResourceTypes.has(ResourceType.gateway)
        ) {
          this._resetSettingsState(
            [
              newViolationConfigurationField.resourceCreatedAt,
              newViolationConfigurationField.resourceBusinessCriticality,
              newViolationConfigurationField.resourceLastModifiedBy,
            ],
            configurationFieldState.irrelevant
          );
        }
        if (
          !selectedResourceTypesArray.every((resourceType) => ppResourceTypesSupportingGenAiFiler.has(resourceType))
        ) {
          this._resetSettingsState(
            [newViolationConfigurationField.resourceGenerativeAiOptions],
            configurationFieldState.irrelevant
          );
        }
        break;
      case Platform.workato:
        if (
          !(
            (selectedResourceTypes.size == 1 || selectedResourceTypes.size == 2) &&
            selectedResourceTypesArray.every((value) => workatoResourceTypesSupportingConnectorsFiler.has(value))
          )
        ) {
          this._resetSettingsState([newViolationConfigurationField.connectors], configurationFieldState.irrelevant);
        }
        this._resetSettingsState(
          [
            newViolationConfigurationField.resourceCreatedBy,
            newViolationConfigurationField.resourceCreatedAt,
            newViolationConfigurationField.resourceSecurityRisk,
            newViolationConfigurationField.resourceBusinessCriticality,
            newViolationConfigurationField.resourceLastModifiedBy,
            newViolationConfigurationField.resourceGenerativeAiOptions,
          ],
          configurationFieldState.irrelevant
        );
        break;
      case Platform.powerBI:
        this._resetSettingsState(
          [
            newViolationConfigurationField.resourceCreatedBy,
            newViolationConfigurationField.resourceCreatedAt,
            newViolationConfigurationField.resourceStatus,
            newViolationConfigurationField.resourceBusinessCriticality,
            newViolationConfigurationField.resourceSecurityRisk,
            newViolationConfigurationField.resourceLastModifiedBy,
            newViolationConfigurationField.resourceFirstSeenAt,
            newViolationConfigurationField.resourceGenerativeAiOptions,
            newViolationConfigurationField.connectors,
          ],
          configurationFieldState.irrelevant
        );
        break;
      // platform that do not support inventory at all
      default:
        this._resetSettingsState(
          [
            newViolationConfigurationField.resourceCreatedBy,
            newViolationConfigurationField.resourceCreatedAt,
            newViolationConfigurationField.resourceStatus,
            newViolationConfigurationField.resourceBusinessCriticality,
            newViolationConfigurationField.resourceSecurityRisk,
            newViolationConfigurationField.resourceLastModifiedBy,
            newViolationConfigurationField.resourceFirstSeenAt,
            newViolationConfigurationField.resourceGenerativeAiOptions,
          ],
          configurationFieldState.irrelevant
        );
    }
  }

  private _initSelectedConfigurationFields(newViolationTriggerModel: NewViolationFoundTriggerConfigModel) {
    if (newViolationTriggerModel.ruleIds && newViolationTriggerModel.ruleIds.length > 0) {
      this.allConfigurationFieldsState[newViolationConfigurationField.violationRule] = configurationFieldState.selected;
    }
    if (newViolationTriggerModel.resourceTypes && newViolationTriggerModel.resourceTypes.length > 0) {
      this.allConfigurationFieldsState[newViolationConfigurationField.resourceTypes] = configurationFieldState.selected;
    }
    if ((newViolationTriggerModel.ruleTags && newViolationTriggerModel.ruleTags.length > 0) || this._isNewTrigger) {
      this.allConfigurationFieldsState[newViolationConfigurationField.ruleTag] = configurationFieldState.selected;
    }
    if (newViolationTriggerModel.severity && newViolationTriggerModel.severity.length > 0) {
      this.allConfigurationFieldsState[newViolationConfigurationField.severity] = configurationFieldState.selected;
    }
    if (newViolationTriggerModel.resourceStatus && newViolationTriggerModel.resourceStatus.length > 0) {
      this.allConfigurationFieldsState[newViolationConfigurationField.resourceStatus] =
        configurationFieldState.selected;
    }
    if (newViolationTriggerModel.resourceCreatedBy) {
      this.allConfigurationFieldsState[newViolationConfigurationField.resourceCreatedBy] =
        configurationFieldState.selected;
    }
    if (newViolationTriggerModel.resourceLastModifiedBy) {
      this.allConfigurationFieldsState[newViolationConfigurationField.resourceLastModifiedBy] =
        configurationFieldState.selected;
    }
    if (newViolationTriggerModel.resourceCreatedSince || newViolationTriggerModel.resourceCreatedUntil) {
      this.allConfigurationFieldsState[newViolationConfigurationField.resourceCreatedAt] =
        configurationFieldState.selected;
    }
    if (newViolationTriggerModel.resourceFirstSeenSince || newViolationTriggerModel.resourceFirstSeenUntil) {
      this.allConfigurationFieldsState[newViolationConfigurationField.resourceFirstSeenAt] =
        configurationFieldState.selected;
    }
    if (newViolationTriggerModel.resourceSecurityRisk && newViolationTriggerModel.resourceSecurityRisk.length > 0) {
      this.allConfigurationFieldsState[newViolationConfigurationField.resourceSecurityRisk] =
        configurationFieldState.selected;
    }
    if (
      newViolationTriggerModel.resourceBusinessCriticality &&
      newViolationTriggerModel.resourceBusinessCriticality.length > 0
    ) {
      this.allConfigurationFieldsState[newViolationConfigurationField.resourceBusinessCriticality] =
        configurationFieldState.selected;
    }
    if (
      newViolationTriggerModel.resourceGenerativeAiOptions &&
      newViolationTriggerModel.resourceGenerativeAiOptions.length > 0
    ) {
      this.allConfigurationFieldsState[newViolationConfigurationField.resourceGenerativeAiOptions] =
        configurationFieldState.selected;
    }
    if (newViolationTriggerModel.connectors && newViolationTriggerModel.connectors.length > 0) {
      this.allConfigurationFieldsState[newViolationConfigurationField.connectors] = configurationFieldState.selected;
    }
    if (
      (newViolationTriggerModel.labels && newViolationTriggerModel.labels.labelsIds.length > 0) ||
      this._isNewTrigger
    ) {
      this.allConfigurationFieldsState[newViolationConfigurationField.labels] = configurationFieldState.selected;
    }
    for (const field in this.allConfigurationFieldsState) {
      if (this.allConfigurationFieldsState[field] == configurationFieldState.selected) {
        this.selectedConfiguration.push(field);
      }
    }
  }

  private _resetSettingsState(settings: string[], state: configurationFieldState) {
    for (const index in settings) {
      this._resetSettingState(settings[index], state);
    }
  }

  private _resetSettingState(setting: string, state: configurationFieldState) {
    this.allConfigurationFieldsState[setting] = state;
    const index = this.selectedConfiguration.indexOf(setting);
    if (index > -1) {
      this.selectedConfiguration.splice(index, 1);
    }
    this._resetFieldValue(setting);
  }

  private _resetFieldValue(setting: string) {
    if (setting == newViolationConfigurationField.resourceBusinessCriticality) {
      this.fg.controls['resourceBusinessCriticality'].setValue([]);
    } else if (setting == newViolationConfigurationField.resourceGenerativeAiOptions) {
      this.fg.controls['resourceGenerativeAiOptions'].setValue([]);
    } else if (setting == newViolationConfigurationField.resourceCreatedAt) {
      this.fg.controls['resourceCreatedSince'].setValue(null);
      this.fg.controls['resourceCreatedUntil'].setValue(null);

      this.createdSinceDatesBehavioralSubject$.next(undefined);
      this.createdUntilDatesBehavioralSubject$.next(undefined);
    } else if (setting == newViolationConfigurationField.connectors) {
      this.updateMultiSelectControl('connectors', []);
    } else if (setting == newViolationConfigurationField.resourceLastModifiedBy) {
      this.fg.controls['resourceLastModifiedBy'].setValue('');
    } else if (setting == newViolationConfigurationField.resourceSecurityRisk) {
      this.fg.controls['resourceSecurityRisk'].setValue([]);
    } else if (setting == newViolationConfigurationField.resourceCreatedBy) {
      this.fg.controls['resourceCreatedBy'].setValue('');
    } else if (setting == newViolationConfigurationField.resourceFirstSeenAt) {
      this.fg.controls['resourceFirstSeenSince'].setValue(null);
      this.fg.controls['resourceFirstSeenUntil'].setValue(null);

      this.firstSeenSinceDatesBehavioralSubject$.next(undefined);
      this.firstSeenUntilDatesBehavioralSubject$.next(undefined);
    } else if (setting == newViolationConfigurationField.severity) {
      this.fg.controls['severity'].setValue([]);
    } else if (setting == newViolationConfigurationField.resourceStatus) {
      this.updateMultiSelectControl('resourceStatus', []);
    } else if (setting == newViolationConfigurationField.violationRule) {
      this.fg.controls['ruleIds'].setValue([]);
      this.multipleResourceTypesFromRulesWarningVisible = false;
      this._setIrrelevantConfigurationFields(new Set<ResourceType>());
    } else if (setting == newViolationConfigurationField.ruleTag) {
      this.fg.controls['ruleTags'].setValue([]);
    } else if (setting == newViolationConfigurationField.labels) {
      this.fg.controls['labelInclusionStatusControl'].setValue(null);
      this.fg.controls['labels'].setValue(null);
    } else if (setting == newViolationConfigurationField.resourceTypes) {
      this.fg.controls['resourceTypes'].setValue([]);
      this.multipleResourceTypesWarningVisible = false;
      this._setIrrelevantConfigurationFields(new Set<ResourceType>());
    }
  }

  private _checkFormIsValid(): boolean {
    if (this.selectedConfiguration.indexOf('violationRule') > -1 && this.fg.get('ruleIds')?.value.length === 0) {
      return false;
    }
    if (this.selectedConfiguration.indexOf('resourceTypes') > -1 && this.fg.get('resourceTypes')?.value.length === 0) {
      return false;
    }
    if (this.selectedConfiguration.indexOf('ruleTag') > -1 && this.fg.get('ruleTags')?.value.length === 0) {
      return false;
    }
    if (this.selectedConfiguration.indexOf('severity') > -1 && this.fg.get('severity')?.value.length === 0) {
      return false;
    }
    if (this.selectedConfiguration.indexOf('connectors') > -1 && this.fg.get('connectors')?.value.length === 0) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceStatus') > -1 &&
      this.fg.get('resourceStatus')?.value.length === 0
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceCreatedBy') > -1 &&
      this.fg.get('resourceCreatedBy')?.value === ''
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceLastModifiedBy') > -1 &&
      this.fg.get('resourceLastModifiedBy')?.value === ''
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceSecurityRisk') > -1 &&
      this.fg.get('resourceSecurityRisk')?.value.length === 0
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceBusinessCriticality') > -1 &&
      this.fg.get('resourceBusinessCriticality')?.value.length === 0
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceGenerativeAiOptions') > -1 &&
      this.fg.get('resourceGenerativeAiOptions')?.value.length === 0
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceCreatedAt') > -1 &&
      !this.fg.get('resourceCreatedSince')?.value &&
      !this.fg.get('resourceCreatedUntil')?.value
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceFirstSeenAt') > -1 &&
      !this.fg.get('resourceFirstSeenSince')?.value &&
      !this.fg.get('resourceFirstSeenUntil')?.value
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('labels') > -1 &&
      (this.fg.get('labels')?.value.labelsIds.length === 0 ||
        this.fg.get('labels')?.value.inclusionStatus === undefined)
    ) {
      return false;
    }
    return true;
  }

  private _createGenerativeAiOptionsSelectOptions(): ISelectOption[] {
    return GenerativeAiOptionsSelectOptions.map(
      (option) =>
        ({
          id: option.value, // In this component the id of the selected option is using as the value
          value: option.value,
          displayName: option.displayName,
        }) as ISelectOption
    );
  }

  private _createRuleTagsSelectOptions(): ISelectOption[] {
    return Object.values(RuleTag).map(
      (value) =>
        ({
          id: value,
          value: value,
          displayName: this._ruleTagDisplayName.transform(value),
        }) as ISelectOption
    );
  }

  private _getLabelFilterModel(labelIds: string[], inclusionStatus: LabelInclusionStatus): PlaybookLabelFilterModel {
    return {
      labelsIds: labelIds,
      inclusionStatus: inclusionStatus,
    };
  }
}
